import { useCallback, useEffect, useMemo, useState } from 'react';
import { destinationsService } from '@/api/services';

const DEFAULT_TTL_MS = 2 * 60 * 1000;
const cache = new Map();

const buildKey = (params) => {
  if (!params || Object.keys(params).length === 0) {
    return 'destinations:list';
  }
  return `destinations:list:${JSON.stringify(params)}`;
};

const extractData = (response) => {
  if (!response) return null;
  return response.data?.data || response.data || [];
};

const shouldRevalidate = (entry, ttlMs) => {
  if (!entry || !entry.data) return true;
  return Date.now() - entry.timestamp > ttlMs;
};

const useDestinationsList = (params = null, options = {}) => {
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  const cacheKey = useMemo(() => buildKey(params), [params]);
  const cached = cache.get(cacheKey);
  const [response, setResponse] = useState(cached?.data ?? null);
  const [loading, setLoading] = useState(!cached?.data);
  const [error, setError] = useState(null);

  const fetchAndUpdate = useCallback(
    (force = false) => {
      const entry = cache.get(cacheKey);
      if (!force && entry?.inFlight) {
        entry.inFlight
          .then((res) => {
            setResponse(res);
            setError(null);
            setLoading(false);
            return res;
          })
          .catch((err) => {
            setError(err);
            setLoading(false);
            throw err;
          });
        return entry.inFlight;
      }

      const request = destinationsService.getAll(params);
      const nextEntry = entry || { data: null, timestamp: 0, inFlight: null };
      nextEntry.inFlight = request;
      cache.set(cacheKey, nextEntry);

      request
        .then((res) => {
          cache.set(cacheKey, { data: res, timestamp: Date.now(), inFlight: null });
          setResponse(res);
          setError(null);
          setLoading(false);
          return res;
        })
        .catch((err) => {
          const existing = cache.get(cacheKey) || { data: null, timestamp: 0, inFlight: null };
          existing.inFlight = null;
          cache.set(cacheKey, existing);
          setError(err);
          setLoading(false);
          throw err;
        });

      return request;
    },
    [cacheKey, params]
  );

  useEffect(() => {
    let active = true;
    const entry = cache.get(cacheKey);

    if (entry?.data) {
      setResponse(entry.data);
      setLoading(false);
    } else {
      setLoading(true);
    }

    if (shouldRevalidate(entry, ttlMs)) {
      fetchAndUpdate().catch(() => {
        if (!active) return;
      });
    }

    return () => {
      active = false;
    };
  }, [cacheKey, ttlMs, fetchAndUpdate]);

  const data = useMemo(() => extractData(response), [response]);

  return {
    response,
    data,
    loading,
    error,
    refresh: () => fetchAndUpdate(true)
  };
};

export default useDestinationsList;
